import type {VirtualAccountApplication} from '@afriex/core/domain/types';

import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminAfriexPerson,
  AdminPaginatedEnvelope,
  AfriexCountry,
  AfriexKYCInfo,
  AfriexQueryParams,
  AfriexVerificationStatus,
  AfriexVirtualAccount,
} from '@/types';
import {AfriexKYCProcessor} from '@/types';
import type {AfriexComplianceDocumentTypes} from '@/types/AfriexComplianceDocumentTypes';
import {getResponseMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

export const kycService = {
  getCountryList: async ({
    limit = 100,
    page = 0,
  }): Promise<AdminPaginatedEnvelope<AfriexCountry[]>> => {
    const params = {
      page,
      limit,
      columns: ['name', 'iso2', 'flag', 'status', 'currency'],
    };

    const url = `/v2/admin/countries?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexCountry[]>>(url);
    return response?.data;
  },
  getKYCList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AdminAfriexPerson[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/kyc?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AdminAfriexPerson[]>>(url);
    return response?.data;
  },
  getKYCDetails: async (userId: string): Promise<AdminAfriexPerson> => {
    const response = await axios.get<AdminAfriexPerson>(
      `/v2/admin/kyc/${userId}`,
    );
    return response.data;
  },
  uploadKYC: async (
    userId: string,
    payload: Partial<AfriexKYCInfo>,
  ): Promise<unknown> => {
    const url = `/v2/admin/kyc/${userId}`;
    const response = await axios.patch(url, payload);
    return response?.data;
  },
  submitKYC: async (
    userId: string,
    processor: AfriexKYCProcessor = AfriexKYCProcessor.Veriff,
  ): Promise<unknown> => {
    const url = `/v2/admin/kyc/verify`;
    const response = await axios.post(url, {
      userId,
      processor,
    });
    return response?.data;
  },
  updateKycStatus: async (
    userId: string,
    status: AfriexVerificationStatus,
    data?: {requestedAdditionalDocuments: AfriexComplianceDocumentTypes[]},
  ): Promise<unknown> => {
    const response = await axios.patch<unknown>(
      `/v2/admin/kyc/status/${userId}`,
      {
        status,
        ...data,
      },
    );
    return response.data;
  },
  deleteVeriffSesion: async (userId: string): Promise<unknown> => {
    const url = `/v2/admin/kyc/session/${userId}`;
    const response = await axios.delete(url, {});
    return response?.data;
  },
  verifyUser: async (
    userId: string,
    status: AfriexVerificationStatus,
  ): Promise<unknown> => {
    const url = `/v2/admin/kyc/status/${userId}`;
    const response = await axios.patch(url, {status});
    return response?.data;
  },
  // The virtual-account compliance application(s) for one user, newest first,
  // including rejected ones. Replaces reading
  // `person.metadata.virtualAccountApplication` off the user payload, which the
  // server stopped writing when applications moved into their own collection.
  //
  // Returns [] rather than throwing on failure, matching getVirtualAccount
  // below: the panel is one section of the KYC page, and a failure here should
  // not blank the whole screen.
  getVirtualAccountApplications: async (
    userId: string,
  ): Promise<VirtualAccountApplication[]> => {
    try {
      const url = `/v2/admin/users/virtual-account-applications/${userId}`;
      const response = await axios.get<VirtualAccountApplication[]>(url);
      return response.data ?? [];
    } catch (e) {
      showErrorMessage(
        getResponseMessage(e) || 'Error getting virtual account application',
      );
      console.error(e);
      return [];
    }
  },
  getVirtualAccount: async (
    userId: string,
  ): Promise<AfriexVirtualAccount[] | undefined> => {
    try {
      const url = `/v2/admin/users/virtual-account/${userId}`;
      const response = await axios.get<AfriexVirtualAccount[]>(url);
      return response.data;
    } catch (e) {
      showErrorMessage(
        getResponseMessage(e) || 'Error getting virtual account information',
      );
      console.error(e);
    }
  },
};
